import { faKeyboard } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useState } from 'react';

import { HIDE_PHI_SHORTCUT_LABEL } from '@/hooks/useHidePhiHotkey';
import Modal from '@/ui/components/Modal';

const isMac = typeof navigator !== 'undefined' && /mac/i.test(navigator.platform);
const modKey = isMac ? '⌘' : 'Ctrl';

const sections = [
  {
    id: 'search',
    label: 'People',
    description:
      'Find a person quickly from anywhere, including while you are already on a profile page.',
    groups: [
      {
        heading: 'Find a person',
        items: [
          { keys: ['Type a name'], description: 'Search for people from any page' },
          { keys: ['↑ ↓'], description: 'Move between search results' },
          { keys: ['Enter'], description: 'Open the highlighted person profile' },
          { keys: ['Tab'], description: 'Switch tabs (People → Person → Workspace)' },
          { keys: ['Shift+Tab'], description: 'Switch tabs in reverse order' },
          { keys: ['Esc'], description: 'Close the command palette' }
        ]
      }
    ]
  },
  {
    id: 'profile',
    label: 'Person',
    description:
      "Navigate to a person's sections. On a person page the command palette opens here automatically.",
    groups: [
      {
        heading: 'Navigate sections',
        items: [
          { keys: ['Type'], description: 'Filter sections by name (e.g. "doc" → Documents)' },
          { keys: ['↑ ↓'], description: 'Move between sections' },
          { keys: ['Enter'], description: 'Jump to the highlighted section' },
          { keys: ['Enter (blank)'], description: 'Go directly to Person overview' },
          { keys: ['Tab'], description: 'Switch tabs (People → Person → Workspace)' },
          { keys: ['Shift+Tab'], description: 'Switch tabs in reverse order' },
          { keys: ['Esc'], description: 'Close the command palette' },
          {
            keys: ['Find different person'],
            description: 'Switch to People tab to look up a different person'
          }
        ]
      }
    ]
  },
  {
    id: 'workspace',
    label: 'Workspace',
    description:
      'Jump to any workspace area. The command palette opens here by default on non-person pages.',
    groups: [
      {
        heading: 'Navigate',
        items: [
          { keys: ['Type'], description: 'Filter workspaces by name' },
          { keys: ['↑ ↓'], description: 'Move between options' },
          { keys: ['Enter'], description: 'Jump to the highlighted workspace' },
          { keys: ['Tab'], description: 'Switch tabs (People → Person → Workspace)' },
          { keys: ['Shift+Tab'], description: 'Switch tabs in reverse order' },
          { keys: ['Esc'], description: 'Close the command palette' },
          {
            keys: ['Results list'],
            description:
              'Available workspace results may vary based on permissions and configuration'
          }
        ]
      }
    ]
  }
];

const Kbd = ({ children }: { children: string }) => (
  <kbd className="inline-block px-1.5 py-0.5 text-xs font-mono bg-gray-100 border border-gray-300 rounded shadow-sm whitespace-nowrap">
    {children}
  </kbd>
);

export const KeyboardShortcutsTrigger = ({ onClick }: { onClick: () => void }) => (
  <button type="button" onClick={onClick}>
    <FontAwesomeIcon className="mr-2" icon={faKeyboard} />
    Keyboard Shortcuts
  </button>
);

export const KeyboardShortcutsModal = ({
  open,
  onClose
}: {
  open: boolean;
  onClose: () => void;
}) => {
  const [activeTab, setActiveTab] = useState<string>(sections[0].id);
  const active = sections.find((s) => s.id === activeTab)!;

  return (
    <Modal open={open} onClose={onClose} title="Keyboard Shortcuts" button={{ children: 'Close' }}>
      <div className="flex h-[24rem] flex-col overflow-hidden">
        <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded">
          <table className="w-full text-sm">
            <tbody>
              <tr className="border-b border-gray-100 last:border-0">
                <td className="py-1.5 pr-4 align-top">
                  <span className="flex gap-1 flex-wrap">
                    <Kbd>{modKey + '+K'}</Kbd>
                  </span>
                </td>
                <td className="py-1.5 text-gray-600 align-top">Open the command palette</td>
              </tr>
              <tr className="border-b border-gray-100 last:border-0">
                <td className="py-1.5 pr-4 align-top">
                  <span className="flex gap-1 flex-wrap">
                    <Kbd>{HIDE_PHI_SHORTCUT_LABEL}</Kbd>
                  </span>
                </td>
                <td className="py-1.5 text-gray-600 align-top">Hide or show PHI on screen</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="flex gap-2 mb-4 border-b border-gray-200">
          {sections.map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => setActiveTab(s.id)}
              className={`px-3 py-1.5 text-sm font-medium rounded-t -mb-px border border-b-0 transition-colors ${
                activeTab === s.id
                  ? 'bg-white border-gray-200 text-gray-900'
                  : 'bg-gray-50 border-transparent text-gray-500 hover:text-gray-700'
              }`}>
              {s.label}
            </button>
          ))}
        </div>

        <p className="text-xs text-gray-500 mb-4">{active.description}</p>

        <div className="flex-1 min-h-0 overflow-y-auto pr-1">
          <div className="space-y-5 text-sm text-gray-700">
            {active.groups.map((group) => (
              <div key={group.heading}>
                <h3 className="font-semibold text-gray-900 mb-2 text-xs uppercase tracking-wide">
                  {group.heading}
                </h3>
                <table className="w-full">
                  <tbody>
                    {group.items.map((item) => (
                      <tr key={item.description} className="border-b border-gray-100 last:border-0">
                        <td className="py-1.5 pr-4 align-top">
                          <span className="flex gap-1 flex-wrap">
                            {item.keys.map((k) => (
                              <Kbd key={k}>{k}</Kbd>
                            ))}
                          </span>
                        </td>
                        <td className="py-1.5 text-gray-600 align-top">{item.description}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Modal>
  );
};
